import { Link } from 'react-router-dom'
import { BookOpen, CheckCircle, Clock, Award, Users, GraduationCap } from 'lucide-react'
import Hero from '../../components/Hero'
import SEO from '../../components/SEO'

export default function Theology() {
  const requirements = [
    'High school diploma or equivalent',
    'Personal statement of faith',
    'Two letters of recommendation from church leaders',
    'Completed application form',
    'Interview with admissions committee'
  ]

  const courses = [
    'Biblical Studies & Hermeneutics',
    'Systematic Theology',
    'Church History',
    'Pastoral Ministry & Counseling',
    'Homiletics & Preaching',
    'Christian Ethics',
    'Missions & Evangelism',
    'Spiritual Formation'
  ]

  const careers = [
    'Pastor/Minister',
    'Missionary',
    'Church Administrator',
    'Youth Pastor',
    'Chaplain',
    'Christian Educator',
    'Ministry Leader',
    'Theological Researcher'
  ]

  return (
    <>
      <SEO 
        title="Theology Program - Rudovia Life College"
        description="Study theology at Rudovia Life College. Comprehensive biblical education preparing you for ministry and spiritual leadership. Accredited theology program in Africa."
        keywords="theology program Africa, biblical studies, ministry training, theology college Zimbabwe, Christian education, pastoral training"
      />
      <div className="pt-20">
        <Hero
          title="Theology Program"
          subtitle="Deepen your understanding of Scripture and prepare for effective ministry"
          image="/assets/images/pexels-pixabay-372326.jpg"
          height="h-[400px]"
          primaryButton={{ text: 'Apply Now', link: '/admissions' }}
        />

        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h2 className="heading-2 mb-6">Program Overview</h2>
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  Our Theology program provides a comprehensive foundation in biblical studies, systematic theology, and practical ministry skills. Students engage deeply with Scripture, church history, and theological traditions while developing the character and competencies needed for effective ministry.
                </p>
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  Through rigorous academic study combined with practical ministry experience, graduates are equipped to serve as pastors, missionaries, educators, and church leaders who can effectively communicate biblical truth and shepherd God's people.
                </p>
                <div className="flex items-center space-x-8 mt-8">
                  <div className="flex items-center space-x-2">
                    <Clock className="w-6 h-6 text-secondary" />
                    <div>
                      <div className="font-bold text-primary">Duration</div>
                      <div className="text-gray-600">3-4 Years</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Award className="w-6 h-6 text-secondary" />
                    <div>
                      <div className="font-bold text-primary">Qualification</div>
                      <div className="text-gray-600">Bachelor of Theology</div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <img 
                  src="/assets/images/pexels-pixabay-372326.jpg" 
                  alt="Theology Studies" 
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-50 rounded-xl p-8">
                <BookOpen className="w-12 h-12 text-secondary mb-4" />
                <h3 className="text-2xl font-bold text-primary mb-4">Core Courses</h3>
                <ul className="space-y-2">
                  {courses.map((course, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{course}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <GraduationCap className="w-12 h-12 text-secondary mb-4" />
                <h3 className="text-2xl font-bold text-primary mb-4">Admission Requirements</h3>
                <ul className="space-y-2">
                  {requirements.map((req, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <Users className="w-12 h-12 text-secondary mb-4" />
                <h3 className="text-2xl font-bold text-primary mb-4">Career Opportunities</h3>
                <ul className="space-y-2">
                  {careers.map((career, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{career}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-primary text-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Why Study Theology at Rudovia?</h2>
              <div className="grid md:grid-cols-2 gap-8 mt-12">
                <div className="text-left">
                  <h3 className="text-2xl font-bold mb-3 text-secondary">Biblical Foundation</h3>
                  <p className="text-gray-200">
                    Grounded in sound biblical interpretation and orthodox Christian theology, our program equips you with deep knowledge of Scripture.
                  </p>
                </div>
                <div className="text-left">
                  <h3 className="text-2xl font-bold mb-3 text-secondary">Practical Ministry</h3>
                  <p className="text-gray-200">
                    Gain hands-on experience through internships, preaching opportunities, and ministry practicums in local churches.
                  </p>
                </div>
                <div className="text-left">
                  <h3 className="text-2xl font-bold mb-3 text-secondary">Expert Faculty</h3>
                  <p className="text-gray-200">
                    Learn from experienced pastors, theologians, and ministry leaders who are passionate about training the next generation.
                  </p>
                </div>
                <div className="text-left">
                  <h3 className="text-2xl font-bold mb-3 text-secondary">Spiritual Formation</h3>
                  <p className="text-gray-200">
                    Develop your character and spiritual life through mentorship, worship, and a supportive Christian community.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-white">
          <div className="container-custom text-center">
            <h2 className="heading-2 mb-6">Ready to Answer the Call?</h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Take the next step in your journey toward ministry. Apply to our Theology program today.
            </p>
            <Link to="/admissions" className="btn-primary">
              Apply Now
            </Link>
          </div>
        </section>
      </div>
    </>
  )
}
